/* ═══════════════════════════════════════════════════════════════
   REVIEW — install the paint on a host's live editor while Review is
   open, keep it fed, and bring the current change into view.

   The plugin is registered on the editor the host already has (Designer
   or Spec Studio canvas) — neither canvas changes its extension list, and
   leaving Review unregisters it, so the editor is exactly as it was.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import {
  reviewPlugin,
  reviewPluginKey,
  setReviewInput,
  type ReviewPluginInput,
} from './reviewPlugin';

export function useReviewDecorations(editor: Editor | null, input: ReviewPluginInput | null): void {
  const active = !!input;

  // Install / uninstall with the editor's life and Review's.
  React.useEffect(() => {
    if (!editor || editor.isDestroyed || !active) return;
    if (!reviewPluginKey.getState(editor.state)) editor.registerPlugin(reviewPlugin(null));
    return () => {
      if (!editor.isDestroyed) editor.unregisterPlugin(reviewPluginKey);
    };
  }, [editor, active]);

  // Feed it (a meta-only transaction — the document is never touched).
  React.useEffect(() => {
    if (!editor || editor.isDestroyed || !input) return;
    if (!reviewPluginKey.getState(editor.state)) return;
    editor.view.dispatch(setReviewInput(editor.state.tr, input));
  }, [editor, input]);

  // The current change into view.
  const currentId = input?.currentId ?? null;
  React.useEffect(() => {
    if (!editor || editor.isDestroyed || !currentId) return;
    const raf = requestAnimationFrame(() => {
      const el = editor.view.dom.querySelector<HTMLElement>(
        `[data-review-change="${CSS.escape(currentId)}"]`
      );
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [editor, currentId]);
}
